import { useEffect, useState } from 'react';
import styles from './Header.module.css';
import { useVisibleSections } from '../../hooks/useVisibleSections';

interface NavSection {
  id: string;
  label: string;
  hash: string;
}

const sections: NavSection[] = [
  { id: 'live', label: 'Live', hash: '#live' },
  { id: 'info', label: 'Info', hash: '#info' },
  { id: 'agenda', label: 'Agenda', hash: '#agenda' },
  { id: 'forge', label: 'Forge Challenge', hash: '#forge' },
  { id: 'quests', label: 'Side Quests', hash: '#quests' },
];

/**
 * Header component provides navigation for the application
 * 
 * Features:
 * - Section navigation with tabs/segmented control
 * - Updates URL hash on navigation
 * - Fire accent for active section
 * - Elevated background with backdrop blur
 * - Sticky positioning on mobile
 * - Minimum 44x44px touch targets
 * 
 * Validates Requirements: 10.2, 10.5
 */
export function Header() {
  const [activeSection, setActiveSection] = useState<string>('live');
  const { isSectionVisible } = useVisibleSections();

  // Filter sections to only show visible ones
  const visibleNavSections = sections.filter(section => 
    isSectionVisible(section.id)
  );

  // Update active section based on URL hash
  useEffect(() => {
    const updateActiveSection = () => {
      const hash = window.location.hash || '#live';
      const sectionId = hash.substring(1);
      setActiveSection(sectionId);
    };

    updateActiveSection();
    window.addEventListener('hashchange', updateActiveSection);

    return () => {
      window.removeEventListener('hashchange', updateActiveSection);
    };
  }, []);

  // Redirect to first visible section if active section becomes hidden
  useEffect(() => {
    if (!isSectionVisible(activeSection) && visibleNavSections.length > 0) {
      const firstVisible = visibleNavSections[0];
      if (firstVisible) {
        setActiveSection(firstVisible.id);
        window.location.hash = firstVisible.hash;
      }
    }
  }, [activeSection, isSectionVisible, visibleNavSections]);

  const handleNavClick = (hash: string, sectionId: string) => {
    // Update URL hash
    window.location.hash = hash;
    setActiveSection(sectionId);

    // Scroll to section smoothly
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <header className={styles.header}>
      <div className={styles.container}>
        <h1 className={styles.title}>Iron Makers Day @ one:demo forge</h1>
        
        <nav className={styles.nav} role="navigation" aria-label="Main navigation">
          {visibleNavSections.map((section) => (
            <a
              key={section.id}
              href={section.hash}
              className={`${styles.navLink} ${activeSection === section.id ? styles.active : ''}`}
              onClick={(e) => {
                e.preventDefault();
                handleNavClick(section.hash, section.id);
              }}
              aria-current={activeSection === section.id ? 'page' : undefined}
            >
              {section.label}
            </a>
          ))}
        </nav>
      </div>
    </header>
  );
}

